import React from 'react';
import { Paper, Grid, Typography, Skeleton } from '@mui/material';
import styled from '@emotion/styled';
import TitleTypeLabel from '../../_common/TitleTypeLabel';
import TitleBrowserV2 from '@/components/TitleDetail/v2/TitleBrowserV2/TitleBrowserV2';
import { insertWordBreaks, pluralizeNoun } from '@/_utils/utils';
import { LabelValue } from '../../_common';
import { selectTitleTypes } from '@/redux/selectors/constantsSelectors';
import { useAppSelector } from '@/redux/hooks';
import SizePreservingLoader from '@/components/_common/SizePreservingLoader/SizePreservingLoader';
import {
  selectActiveTitle,
  selectIsActiveTitleLoading,
  selectIsRootTitleLoading,
  selectRootTitle,
  selectRootTitleIsSeries
} from '@/redux/selectors/titleV2Selectors';
import { useTitleURLParser } from '@/components/TitleDetail/v2/hooks/useTitleURLParser';

const TitleDetailHeaderV2 = () => {
  const isRootTitleLoading = useAppSelector(selectIsRootTitleLoading);
  const isActiveTitleLoading = useAppSelector(selectIsActiveTitleLoading);
  const rootTitle = useAppSelector(selectRootTitle);
  const activeTitle = useAppSelector(selectActiveTitle);
  const titleMap = useAppSelector(selectTitleTypes);
  const isRootEntitySeries = useAppSelector(selectRootTitleIsSeries);
  const activeTitleNonVersionChildren = activeTitle?.relationships.children.filter((child) => child.entity_type !== 'version');
  const activeTitleHasNonVersionChildren = Boolean(activeTitleNonVersionChildren?.length);
  const { path } = useTitleURLParser();

  const isRootActive = path.length === 1;

  const getEndDate = () => {
    if (!isRootEntitySeries) return;
    if (!rootTitle || !rootTitle.end_year)
      return (
        <span style={{ paddingLeft: '.5rem' }}>
          - <span style={{ opacity: 0.5 }}>present</span>
        </span>
      );
    if (rootTitle.end_year) return `- ${rootTitle.end_year}`;
  };

  const showAvailableCount = activeTitleHasNonVersionChildren && Boolean(activeTitle?.relationships.children?.length);

  const childLabel = () => {
    switch (activeTitle?.entity_type) {
      case 'series':
        return pluralizeNoun(titleMap['season']);
      case 'season':
        return pluralizeNoun(titleMap['episode']);
      case 'campaign':
        return pluralizeNoun(titleMap['marketing']);
      case 'marketing':
        return 'Children';
      default:
        return 'Children';
    }
  };

  return (
    <Paper sx={{ py: 2, px: 3 }}>
      <Grid container alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h2" sx={{ fontWeight: 300 }}>
          <SizePreservingLoader
            isLoading={isActiveTitleLoading}
            loader={
              <div>
                <Skeleton width={280} />
              </div>
            }
          >
            {activeTitle?.name && insertWordBreaks(activeTitle.name)}
          </SizePreservingLoader>
        </Typography>
        {rootTitle &&
          !isRootTitleLoading &&
          isRootActive && // Show start/end year only when the root title is active (selected)
          rootTitle.start_year && (
            <Typography variant="h6" sx={{ opacity: 0.6, ml: 1 }}>
              ({rootTitle.start_year}
              {getEndDate()})
            </Typography>
          )}
      </Grid>
      <SizePreservingLoader isLoading={isActiveTitleLoading} loader={<Skeleton width={200} height={32} />}>
        <Grid container alignItems="center">
          {activeTitle?.entity_type && <TitleTypeLabel width="auto" type={activeTitle.entity_type} />}
          {showAvailableCount && (
            <StyledLabelValue color="default" label={`Available ${childLabel()}`} horizontal>
              <strong>{activeTitleNonVersionChildren?.length}</strong>
            </StyledLabelValue>
          )}
        </Grid>
      </SizePreservingLoader>

      <TitleBrowserV2 />
    </Paper>
  );
};

export default TitleDetailHeaderV2;

const StyledLabelValue = styled(LabelValue)`
  margin-right: 20px;
`;
